// Shop-based data models matching backend architecture
export interface Slide {
  id: string;
  name: string;
  description: string;
  // v1.1.5 Internal Notes + Marketing Description feature. All optional (older slides / external
  // hydrated slides may omit them).
  // - marketingDescription: AI-generated customer-facing copy. For CLIENT/VENDOR hydrated slides the
  //   server already places the customer-facing copy in `description` (dual-key), so this is populated
  //   for INTERNAL slides fetched from slide-svc, where it is shown alongside the internal description.
  marketingDescription?: string;
  marketplaceTitle?: string;   // internal Etsy-listing title helper
  marketplaceTags?: string[];  // internal Etsy-listing tags helper (<= 13)
  notes?: string;              // internal-only shop notes (never sent to external roles)
  categories: string[];
  shopId: string;
  images: SlideImage[];
  priceHistory?: PriceRecord[];
  currentListingPrice?: number;
  acquisitionPrice?: number;
  salePrice?: number;
  // External (CLIENT/VENDOR) pricing hint from the hydration projection: most-recent LISTING price
  // only (amount + currency). Set for hydrated external slides; internal slides use priceHistory.
  listingPrice?: number;
  listingCurrency?: string;
  sold: boolean;
  active?: boolean;
  published?: boolean;  // Phase 3b: slide is published for external (CLIENT/VENDOR) eyes; missing => unpublished
  createdAt: string;
  updatedAt: string;
  primaryImage?: SlideImage;
  // Phase 2: Location/Place Association (mutually exclusive)
  locationId?: string;  // UUID reference to shopLocation (only location, not place)
  placeId?: string;     // UUID reference to place within shopLocation (specific place)
  locationName?: string; // Enriched metadata from shop-svc (Phase 2)
  placeName?: string;    // Enriched metadata from shop-svc (Phase 2)
  // Location data
  latitude?: number;
  longitude?: number;
  locationCapturedAt?: string;
  // Legacy compatibility properties
  price?: number;
  desc?: string;
  cat?: string;
  prov?: string;
  retailerId?: string;
  imgUrl?: string;
}

export interface SlideImage {
  id: string;
  filename: string;
  description?: string;
  displayOrder: number;
  uploadedAt: string;
  published?: boolean;  // Phase 3b: image is published for external eyes; missing => unpublished
  /**
   * True = the platform's canonical bulk-import stub rather than a real photo. There is no
   * per-slide object in GCS for these, so anything that reads the image bytes (Marketplace Eval,
   * Quick Analyze) fails against a slide that has nothing else. Missing => false.
   *
   * Server-managed — slide-svc re-applies the stored value on every update.
   */
  placeholder?: boolean;
}

/**
 * True when at least one image is a REAL photo rather than the import placeholder.
 *
 * NOT the same as a non-empty list: an import stub has an image, it just isn't a photo, which is
 * why gating the AI features on `images.length > 0` let them run and fail server-side.
 */
export const hasAnalyzableImage = (images?: SlideImage[]): boolean =>
  (images ?? []).some(img => !img.placeholder);

export interface PriceRecord {
  id: string;
  type: 'ACQUISITION' | 'LISTING' | 'SOLD';
  amount: number;
  currency: string;
  recordedAt: string;
  effectiveDate: string;
  notes?: string;
}

// Subscription status constants
export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'trial_expired';

export interface Subscription {
  billingProvider?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripePriceId?: string;
  status: SubscriptionStatus;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  canceledAt?: string;
  trialEnd?: string;
  trialStartedAt?: string;
  firstPaidAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Shop {
  id: string;
  name: string;
  description?: string;
  type: 'PERSONAL' | 'BUSINESS' | 'BUSINESS_PLUS';
  ownerId: string;
  businessInfo?: string;
  active: boolean;
  subscription?: Subscription;
  createdAt: string;
  updatedAt: string;
}

// Rate Limit types
export type ResourceType =
  | 'SLIDES'
  | 'ACTIVE_MEMBERS'
  | 'ACTIVE_CLIENT_MEMBERS'
  | 'SHOP_LOCATIONS'
  | 'SHOP_PLACES'
  | 'MARKETPLACE_EVALS'
  | 'QUICK_ANALYZES';

export interface RateLimitCheckResult {
  allowed: boolean;
  resourceType: ResourceType;
  currentUsage: number;
  maxAllowed: number;
  remaining: number;
  tierType: string;
  message?: string;
}

export type RateLimitStatuses = {
  [key in ResourceType]?: RateLimitCheckResult;
};

// Custom error class for rate limit exceeded errors
export class RateLimitError extends Error {
  public readonly resourceType: ResourceType;
  public readonly currentUsage: number;
  public readonly maxAllowed: number;
  public readonly tierType: string;
  public readonly rateLimitMessage?: string;

  constructor(data: {
    resourceType: ResourceType;
    currentUsage: number;
    maxAllowed: number;
    tierType: string;
    message?: string;
  }) {
    super(data.message || `Rate limit exceeded for ${data.resourceType}`);
    this.name = 'RateLimitError';
    this.resourceType = data.resourceType;
    this.currentUsage = data.currentUsage;
    this.maxAllowed = data.maxAllowed;
    this.tierType = data.tierType;
    this.rateLimitMessage = data.message;
  }

  static fromResponse(body: any): RateLimitError {
    return new RateLimitError({
      resourceType: body.resourceType || 'SLIDES',
      currentUsage: body.currentUsage || 0,
      maxAllowed: body.maxAllowed || 0,
      tierType: body.tierType || 'UNKNOWN',
      message: body.message,
    });
  }

  static isRateLimitError(error: unknown): error is RateLimitError {
    return error instanceof RateLimitError;
  }
}

// --- CSV Inventory Import (v1.1.6) ---

export interface ImportPreviewResponse {
  jobId: string;
  status: string;
  totalRows: number;
  importableRows: number;
  rejectedRows: number;
  skippedForQuota: number;
  warningCount: number;
  unrecognizedColumns: string[];
}

export interface ImportStatusResponse {
  jobId: string;
  status: string;
  totalRows: number;
  importableRows: number;
  rejectedRows: number;
  skippedForQuota: number;
  warningCount: number;
  processed: number;
  created: number;
  errorMessage?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ImportWarning {
  rowNumber: number; // 1-based data row; 0 = file-level
  message: string;
}

/** Thrown on HTTP 409 from the import upload: the shop already has an import in progress. */
/** One row of the shop's CSV import history (My Shop > Import CSV). */
export interface ImportHistoryItem {
  jobId: string;
  status: string;
  totalRows: number;
  created: number;
  createdAt: string;
  /** Null until a "delete this import's slides" run has been requested. */
  deletionStatus: string | null;
  deletedCount: number;
  keptCount: number;
  deletionCompletedAt: string | null;
}

/**
 * Pre-delete figures. `deletable` counts slides that still hold only the import placeholder;
 * `kept` counts those the user has since added a real photo to, which are never deleted.
 */
export interface ImportDeletionPreview {
  jobId: string;
  deletable: number;
  kept: number;
}

/** Progress of a slide-deletion run, polled after requesting one. */
export interface ImportDeletionStatus {
  jobId: string;
  status: string | null;   // REQUESTED | PROCESSING | COMPLETED | FAILED
  deleted: number;
  kept: number;
  requestedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
}

export class ImportInProgressError extends Error {
  public readonly activeJobId?: string;

  constructor(message: string, activeJobId?: string) {
    super(message);
    this.name = 'ImportInProgressError';
    this.activeJobId = activeJobId;
  }

  static fromResponse(body: any): ImportInProgressError {
    return new ImportInProgressError(
      body?.message || 'An import is already in progress for this shop.',
      body?.activeJobId
    );
  }

  static isImportInProgressError(error: unknown): error is ImportInProgressError {
    return error instanceof ImportInProgressError;
  }
}

export interface ShopMembership {
  id: string;
  shopId: string;
  firebaseUid: string;
  email?: string;
  displayName?: string;
  roles: MembershipRole[];
  active: boolean;
  defaultShop: boolean;
  joinedAt: string;
  updatedAt: string;
}

export type MembershipRole =
  | 'PLATFORM_ADMIN'
  | 'OWNER'
  | 'ADMIN'
  | 'MEMBER'
  | 'VIEWER'
  | 'CLIENT'
  | 'VENDOR';

export interface GeoCoordinates {
  latitude: number;
  longitude: number;
}

export interface StreetAddress {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface ImageMetadata {
  id: string;
  filename: string;
  contentType: string;
  uploadedAt: string;
  uploadedBy: string;
}

export interface Place {
  id: string;
  name: string;
  description?: string;
  geoCoordinates?: GeoCoordinates;
  images: ImageMetadata[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface ShopLocation {
  id: string;
  shopId: string;
  name: string;
  sortOrder: number;
  description?: string;
  streetAddress?: StreetAddress;
  geoCoordinates?: GeoCoordinates;
  images: ImageMetadata[];
  places: Place[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface SlideFormData {
  name?: string;
  description?: string;
  // v1.1.5 Internal Notes + Marketing Description feature (internal edit only).
  marketingDescription?: string;
  marketplaceTitle?: string;
  marketplaceTags?: string[];
  notes?: string;
  categories?: string[];
  shopId?: string;
  images?: SlideImage[];
  priceHistory?: PriceRecord[];
  active?: boolean;
  // Phase 2: Location/Place Association
  locationId?: string;  // UUID reference to shopLocation
  placeId?: string;     // UUID reference to place within shopLocation
  // Location data
  latitude?: number;
  longitude?: number;
  locationCapturedAt?: string;
  // Legacy compatibility properties
  price?: number;
  desc?: string;
  cat?: string;
  prov?: string;
}

export interface CreateSlideWithImagesRequest {
  slide: SlideFormData;
  primaryImage?: File;
  primaryImageDescription?: string;
  additionalImages?: File[];
  additionalImageDescriptions?: string[];
}

export interface SearchFilters {
  nameQuery?: string;
  name?: string; // Legacy compatibility
  categories?: string[];
  category?: string; // Legacy compatibility
  categoriesOperator?: 'AND' | 'OR';
  minPrice?: number;
  maxPrice?: number;
  priceType?: 'CURRENT_LISTING' | 'ACQUISITION' | 'SOLD';
  addedAfter?: string;
  addedBefore?: string;
  imageCountMin?: number;
  imageCountMax?: number;
  hasPrimaryImage?: boolean;
  sold?: boolean;
  hasListingPrice?: boolean;
  available?: boolean;
  active?: boolean;
  owner?: string;
  priceTypes?: string[];
  onlyFavorites?: boolean;
  shopId?: string;
  locationId?: string;
  placeId?: string;
  sortBy?: 'name' | 'acquisitionPrice' | 'listingPrice' | 'soldPrice' | 'createdAt' | 'updatedAt' | 'imageCount' | 'categoryCount' | 'priceRecordCount';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  size?: number;
}

export interface SearchResult<T> {
  items: T[];
  metadata: SearchMetadata;
}

export interface SearchMetadata {
  totalElements: number;
  page: number;
  size: number;
  totalPages?: number;
  executionTimeMs?: number;
  appliedFilters?: Record<string, any>;
}

export interface User {
  uid: string;
  email: string;
  displayName?: string;
}

// UI-specific types
export interface UserContext {
  user: User | null;
  shops: Shop[];
  currentShop: Shop | null;
  userMemberships: ShopMembership[];
  currentMembership: ShopMembership | null;
  loading: boolean;
}

// What the requesting user may do with a collection. Computed per request by slide-svc
// (NamedCollectionService.evaluateAccess), so the UI never re-derives the role matrix.
export interface CollectionPermissions {
  canEdit: boolean;
  canDelete: boolean;
  canChangeVisibility: boolean;
  canShare: boolean;
}

// Named Collections (durable, curated slide groupings). Private to the creator by default; the
// creator can make one shop visible (every internal role can view it, write roles can edit it,
// OWNER/ADMIN/PLATFORM_ADMIN can delete it). See slide-svc/SHOP_VISIBLE_COLLECTIONS_PLAN.md.
// Mirrors slide-svc NamedCollection. slideCount is server-computed (slideRefs.size()).
export interface NamedCollection {
  id: string;
  shopId: string;
  createdByUid: string;
  name: string;
  slideRefs: string[];
  slideCount: number;
  createdAt: string;
  updatedAt: string;
  // Server-derived (slide-svc isShared(), from shareExpiresAt): whether this collection is
  // currently shared as a feed cube. Drives the "Shared" badge + delete-confirmation copy.
  shared?: boolean;
  shareExpiresAt?: string | null;
  // Shop-visible collections. Optional because a server that predates the feature omits them.
  shopVisible?: boolean;
  // Caller-relative, computed per request (never stored).
  createdByMe?: boolean;
  // The creator's CURRENT display name, looked up live; null for your own or when unavailable.
  createdByDisplayName?: string | null;
  permissions?: CollectionPermissions;
}

export interface ShopContextType {
  currentShop: Shop | null;
  userShops: Shop[];
  userMemberships: ShopMembership[];
  currentMembership: ShopMembership | null;
  switchShop: (shopId: string) => void;
  refreshShops: () => Promise<void>;
  canViewPriceData: boolean;
  canManageSlides: boolean;
  canManageShop: boolean;
  canManageMembers: boolean;
  isInternalMember: boolean;
  isPlatformAdmin: boolean;
  userRole: MembershipRole | null;
  loading: boolean;
}

// Background Task types
export interface BackgroundTask {
  id: string;
  taskType: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  slideId: string;
  shopId: string;
  analysisId?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

// Quick Analyze types
export interface QuickAnalyzeResponse {
  shopId: string;
  analysisId: string;
  analyzedAt: string;
  modelVersion: string;
  userProvidedText?: string;  // User's input text for context/questions
  suggestedTitle?: string;
  suggestedEra?: string;
  suggestedMaker?: string;
  materials?: string[];
  stylePeriod?: string;
  conditionNotes?: string;
  distinguishingFeatures?: string;
  collectibilityNotes?: string;
  marketplaceKeywords?: string[];
  categoryTags?: string[];
  // v1.1.5 marketing outputs (generated by the marketing-description CF, stored in quick_analyze).
  marketingTitle?: string;
  marketingDescription?: string;
  marketplaceTags?: string[];
  imageSearchQueries?: string[];
  // Price tag detection (v14)
  priceTagDetected?: boolean;
  detectedPrice?: number;
  detectedCurrency?: string;
  detectedRetailer?: string;
  priceTagImageIndex?: number;
  imageCount: number;
  imageIds: string[];
  processingDurationMs: number;
  convertedToSlideId?: string;
  convertedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TriggerAnalysisResponse {
  analysis_id: string;  // Backend uses snake_case
  status: string;
  message: string;
  timestamp: string;
}

// Marketplace Eval types
export interface MPEResult {
  position: number;
  title: string;
  url: string;
  displayLink: string;
  snippet: string;
  htmlSnippet?: string;
  thumbnailUrl?: string;
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  imageUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogSiteName?: string;
  source?: 'GOOGLE_IMAGES' | 'GOOGLE_LENS';  // v16: Result source tracking
}

export interface PriceEstimate {
  lowPrice: number;
  highPrice: number;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
  currency: string;
  rationale: string | null;
  marketFactors: string[];
  model: string;
  estimatedAt: string;
}

export interface MarketplaceEvalResponse {
  evalId: string;
  analysisId: string;
  searchQuery: string;
  suggestedQuery?: string;
  queryEdited?: boolean;
  searchTimestamp?: string;
  cacheHit: boolean;
  totalResults: number;
  googleImagesCount?: number;  // v16: Google Images result count
  googleLensCount?: number;     // v16: Google Lens result count
  lensImageUrl?: string;        // v16: Public URL of image used for Lens search
  pagesFetched?: number;        // Number of pages fetched from Images API
  priceEstimate?: PriceEstimate | null;  // v18: Gemini AI resale price estimate
  results: MPEResult[];
}